import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAction, useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import Layout from '../components/Layout/Layout';
import SendAppNotification from '../components/Admin/SendAppNotification';
import MeterReadingRequest from '../components/Admin/MeterReadingRequest';
import ReadingsTable from '../components/Dashboard/ReadingsTable';
import ReadingsChart from '../components/Dashboard/ReadingsChart';

interface EditForm {
  name: string;
  email: string;
  mobileNumber: string;
  telephoneNumber: string;
  address: string;
}

interface Popup {
  _id: Id<"sitePopups">;
  title: string;
  message: string;
  phoneNumber?: string;
  showPhone: boolean;
  isActive: boolean;
  viewCount: number;
  dismissCount: number;
  callCount: number;
}
type AdminCustomerLocationState = {
  focusSection?: 'pending-profile-updates';
  submissionId?: string;
};
const MAX_SITE_POPUPS_PER_CUSTOMER = 1;
// Customers with a password reset or email-code change still in progress.
// Module-level so it survives navigating away and back: two such changes
// for one customer must never run at once, or one could undo the other.
const accountChangesInFlight = new Set<string>();
const BUSY_MESSAGE = 'A change to this customer\'s sign-in is still in progress. Please wait a moment.';

const AdminCustomerPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>({
    name: '',
    email: '',
    mobileNumber: '',
    telephoneNumber: '',
    address: ''
  });
  const [resetStep, setResetStep] = useState<'idle' | 'confirm' | 'working'>('idle');
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [tempPasswordCopied, setTempPasswordCopied] = useState(false);
  const [emailCodesStep, setEmailCodesStep] = useState<'idle' | 'confirm' | 'working'>('idle');
  const [emailCodesError, setEmailCodesError] = useState<string | null>(null);
  // Counts visits to customer records. A request only updates the page if it
  // was started on the current visit, so a late reply from an earlier visit
  // (even to the same customer, after A → B → A) is dropped.
  const visitRef = useRef(0);

  // The reset panel state belongs to one customer — clear it when the admin
  // navigates to a different customer record.
  useEffect(() => {
    visitRef.current += 1;
    setResetStep('idle');
    setTempPassword(null);
    setResetError(null);
    setTempPasswordCopied(false);
    setEmailCodesStep('idle');
    setEmailCodesError(null);
  }, [id]);
  const [showPopupForm, setShowPopupForm] = useState(false);
  const [popupForm, setPopupForm] = useState({
    title: 'Important Notice',
    message: 'Please give us a call about your system.',
    phoneNumber: '',
    showPhone: true
  });

  // Convex queries and mutations
  const customerId = id as Id<"users">;
  const customerData = useQuery(api.users.getCustomerWithReadings, id ? { id: customerId } : "skip");
  const popups = useQuery(api.popups.getAllForUser, id ? { userId: customerId } : "skip");
  const popupCount = popups?.length ?? 0;
  const hasReachedPopupLimit = popupCount >= MAX_SITE_POPUPS_PER_CUSTOMER;
  const meterSubmissions = useQuery(
    api.meterSubmissions.getForUserAdmin,
    id ? { userId: customerId, limit: 20 } : "skip"
  );

  const locationState = location.state as AdminCustomerLocationState | null;
  const pendingProfileUpdates = useQuery(
    api.updateRequests.getForUserAdmin,
    id ? { userId: customerId, limit: 20 } : "skip"
  );
  useEffect(() => {
    if (locationState?.focusSection !== 'pending-profile-updates') return;
    const el = document.getElementById('pending-profile-updates');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [locationState?.focusSection, pendingProfileUpdates?.length, id]);

  const updateCustomerMutation = useMutation(api.users.updateCustomer);
  const createPopupMutation = useMutation(api.popups.create);
  const updatePopupMutation = useMutation(api.popups.update);
  const deletePopupMutation = useMutation(api.popups.deletePopup);
  const reviewMeterSubmissionMutation = useMutation(api.meterSubmissions.review);
  const reviewProfileUpdateMutation = useMutation(api.updateRequests.review);
  const syncExternalReadings = useAction(api.externalReadings.syncExternalReadingsForUsername);
  const resetCustomerPassword = useAction(api.adminPasswordReset.resetCustomerPassword);
  const emailCodes = useQuery(api.adminSignInEmail.getStatus, id ? { userId: customerId } : "skip");
  const enableCustomerEmailCodes = useAction(api.adminPasswordReset.enableCustomerEmailCodes);
  const syncedUsernameRef = useRef<string | null>(null);

  useEffect(() => {
    const username = customerData?.user?.username;
    if (!username || syncedUsernameRef.current === username) return;

    syncedUsernameRef.current = username;
    syncExternalReadings({ username }).catch((error) => {
      console.error('Failed to sync customer external readings', error);
    });
  }, [customerData?.user?.username, syncExternalReadings]);

  const handleUpdateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      const result = await updateCustomerMutation({
        id: customerId,
        name: editForm.name || undefined,
        email: editForm.email || undefined,
        mobileNumber: editForm.mobileNumber || undefined,
        telephoneNumber: editForm.telephoneNumber || undefined,
        address: editForm.address || undefined,
      });

      if (result.success) {
        setEditing(false);
      } else {
        alert(result.error || 'Failed to update customer details');
      }
    } catch (error: any) {
      console.error('Error updating customer:', error);
      alert(error.message || 'Failed to update customer details');
    }
  };

  const handleResetPassword = async () => {
    if (!id) return;
    if (accountChangesInFlight.has(id)) { setResetError(BUSY_MESSAGE); return; }
    // A late response must never show a temp password from an earlier visit
    // (another customer, or one since replaced) — drop it unless this visit
    // is still current.
    const requestedId = id;
    const visit = visitRef.current;
    const staleResponse = () => visitRef.current !== visit;
    accountChangesInFlight.add(requestedId);
    setResetStep('working');
    setResetError(null);
    try {
      const result = await resetCustomerPassword({ userId: requestedId as Id<"users"> });
      if (staleResponse()) return;
      if (result.success && result.tempPassword) {
        setTempPassword(result.tempPassword);
        setTempPasswordCopied(false);
      } else {
        setResetError(result.error || 'Password reset failed');
      }
    } catch (error: any) {
      console.error('Error resetting customer password:', error);
      if (!staleResponse()) {
        setResetError(error.message || 'Password reset failed');
      }
    } finally {
      accountChangesInFlight.delete(requestedId);
      if (!staleResponse()) setResetStep('idle');
    }
  };

  const handleEnableEmailCodes = async () => {
    if (!id) return;
    if (accountChangesInFlight.has(id)) { setEmailCodesError(BUSY_MESSAGE); return; }
    const requestedId = id;
    const visit = visitRef.current;
    const staleResponse = () => visitRef.current !== visit;
    accountChangesInFlight.add(requestedId);
    setEmailCodesStep('working');
    setEmailCodesError(null);
    try {
      const result = await enableCustomerEmailCodes({ userId: requestedId as Id<"users"> });
      if (staleResponse()) return;
      // On success the status query updates and the panel shows "on". Any
      // temporary password still on screen has just been replaced.
      if (result.success) {
        setTempPassword(null);
        setTempPasswordCopied(false);
      } else {
        setEmailCodesError(result.error || 'Could not turn on email codes');
      }
    } catch (error: any) {
      console.error('Error turning on email codes:', error);
      if (!staleResponse()) {
        setEmailCodesError(error.message || 'Could not turn on email codes');
      }
    } finally {
      accountChangesInFlight.delete(requestedId);
      if (!staleResponse()) setEmailCodesStep('idle');
    }
  };

  const handleCopyTempPassword = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword);
      setTempPasswordCopied(true);
    } catch (error) {
      console.error('Failed to copy temporary password:', error);
      setResetError('Could not copy to clipboard — select the password and copy it manually.');
    }
  };

  const handleCreatePopup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    if (hasReachedPopupLimit) {
      alert(`Maximum of ${MAX_SITE_POPUPS_PER_CUSTOMER} site popups reached for this customer.`);
      setShowPopupForm(false);
      return;
    }

    try {
      const result = await createPopupMutation({
        userId: customerId,
        title: popupForm.title,
        message: popupForm.message,
        phoneNumber: popupForm.phoneNumber || undefined,
        showPhone: popupForm.showPhone,
      });

      if (!result?.success) {
        alert(result.error || "Failed to create popup");
        return;
      }

      setShowPopupForm(false);
      setPopupForm({
        title: "Important Notice",
        message: "Please give us a call about your system.",
        phoneNumber: "",
        showPhone: true,
      });
    } catch (error: any) {
      console.error("Error creating popup:", error);
      alert(error.message || "Failed to create popup");
    }
  };

  const handleTogglePopup = async (popupId: Id<"sitePopups">, currentActive: boolean) => {
    try {
      await updatePopupMutation({
        id: popupId,
        isActive: !currentActive,
      });
    } catch (error) {
      console.error('Error toggling popup:', error);
    }
  };

  const handleDeletePopup = async (popupId: Id<"sitePopups">) => {
    if (!confirm('Are you sure you want to delete this popup?')) return;
    try {
      await deletePopupMutation({ id: popupId });
    } catch (error) {
      console.error('Error deleting popup:', error);
    }
  };

  const handleReviewUpdateProfileSubmission = async (
    submissionId: any,
    decision: 'accepted' | 'declined'
  ) => {
    try {
      const reviewerNotes = window.prompt(
        decision === 'accepted' ? 'Optional approval notes' : 'Optional decline notes'
      );

      const result = await reviewProfileUpdateMutation({
        id: submissionId,
        decision,
        reviewerNotes: reviewerNotes || undefined,
      });

      if (!result.success) {
        alert(result.error || 'Unable to review submission');
      }
    } catch (error: any) {
      console.error('Error reviewing meter submission:', error);
      alert(error.message || 'Unable to review submission');
    }
  };
  const handleReviewMeterSubmission = async (
    submissionId: any,
    decision: 'approved' | 'rejected'
  ) => {
    try {
      const reviewerNotes = window.prompt(
        decision === 'approved' ? 'Optional approval notes' : 'Optional rejection notes'
      );

      const result = await reviewMeterSubmissionMutation({
        id: submissionId,
        decision,
        reviewerNotes: reviewerNotes || undefined,
      });

      if (!result.success) {
        alert(result.error || 'Unable to review submission');
      }
    } catch (error: any) {
      console.error('Error reviewing meter submission:', error);
      alert(error.message || 'Unable to review submission');
    }
  };

  const startEditing = () => {
    if (customerData?.user) {
      setEditForm({
        name: customerData.user.name || '',
        email: customerData.user.email || '',
        mobileNumber: customerData.user.mobileNumber || '',
        telephoneNumber: customerData.user.telephoneNumber || '',
        address: customerData.user.address || ''
      });
      setEditing(true);
    }
  };

  if (customerData === undefined) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!customerData) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p>Customer not found</p>
          <button
            onClick={() => navigate('/admin')}
            className="mt-4 bg-gray-800 text-white py-2 px-4 rounded-md hover:bg-gray-700"
          >
            Back to Admin
          </button>
        </div>
      </Layout>
    );
  }

  const { user: customer, readings } = customerData;
  // Codes already on, but a later reset (or an imported password) left a
  // password change the customer cannot complete after a code sign-in.
  const stuckOnPassword = emailCodes?.status === 'enabled' && emailCodes.passwordChangePending;
  const canApply = emailCodes?.status === 'available' || stuckOnPassword;
  const co2Saved = (readings.totalGenerated * 0.433 / 1000).toFixed(2);
  const formattedWeekly = readings.weekly.map((r: any) => ({
    id: r._id,
    date: r.date,
    daily_kwh: r.dailyKwh,
    total_kwh: r.totalKwh,
  }));
  const formattedMonthly = readings.monthly.map((r: any) => ({
    id: r._id,
    date: r.date,
    daily_kwh: r.dailyKwh,
    total_kwh: r.totalKwh,
  }));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-shade-green-500 transition-colors duration-200"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Admin
          </button>
        </div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-light">
              Viewing {customer.name || customer.username} ({customer.username})
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              ⌞Last logged in: {customer.lastLogin
                ? new Date(customer.lastLogin).toLocaleString('en-GB', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
                : 'Never'}⌝
            </p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="bg-gray-800 text-white py-2 px-4 rounded-md hover:bg-gray-700"
          >
            Find another customer
          </button>
        </div>

        {/* Customer Details Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-medium font-sans">Customer Details</h2>
            <div className="space-x-3">
              {!editing && (
                <button
                  onClick={startEditing}
                  className="bg-[#16A34A]  text-white py-2 px-4 rounded-md hover:bg-shade-green-700"
                >
                  Edit Details
                </button>
              )}
            </div>
          </div>

          {editing ? (
            <form onSubmit={handleUpdateCustomer} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    value={editForm.mobileNumber}
                    onChange={(e) => setEditForm({ ...editForm, mobileNumber: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telephone Number
                  </label>
                  <input
                    type="tel"
                    value={editForm.telephoneNumber}
                    onChange={(e) => setEditForm({ ...editForm, telephoneNumber: e.target.value })}
                    className="w-full"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#16A34A]  text-white rounded-md hover:bg-shade-green-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Username</p>
                <p className="font-medium">{customer.username}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{customer.name || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">
                  {customer.email}
                  {customer.emailVerified && (
                    <span className="ml-2 text-green-600 text-sm">✓ Verified</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Mobile</p>
                <p className="font-medium">
                  {customer.mobileNumber || 'Not provided'}
                  {customer.mobileVerified && customer.mobileNumber && (
                    <span className="ml-2 text-green-600 text-sm">✓ Verified</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Telephone</p>
                <p className="font-medium">{customer.telephoneNumber || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Last Details Update</p>
                <p className="font-medium">
                  {customer.lastDetailsUpdate
                    ? new Date(customer.lastDetailsUpdate).toLocaleDateString()
                    : 'Never'}
                </p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-medium">{customer.address || 'Not provided'}</p>
              </div>
            </div>
          )}

          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div>
                <p className="text-sm font-medium text-amber-900">Password Reset</p>
                <p className="text-sm text-amber-800 mt-1">
                  Generates a temporary password, logs the customer out of all devices,
                  and requires them to set a new password at their next login.
                </p>
              </div>
              {resetStep === 'idle' && !tempPassword && (
                <button
                  onClick={() => { setResetStep('confirm'); setResetError(null); }}
                  className="bg-amber-600 text-white py-2 px-4 rounded-md hover:bg-amber-700 shrink-0"
                >
                  Reset Password
                </button>
              )}
            </div>

            {resetStep === 'confirm' && (
              <div className="mt-3 p-3 bg-white border border-amber-300 rounded-md">
                <p className="text-sm text-gray-800">
                  Reset the password for <strong>{customer.name || customer.username}</strong>?
                  Their current password will stop working immediately.
                  Their saved passkeys will also be removed, and all devices signed out.
                </p>
                <div className="mt-3 flex space-x-3">
                  <button
                    onClick={handleResetPassword}
                    className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700"
                  >
                    Yes, reset password
                  </button>
                  <button
                    onClick={() => setResetStep('idle')}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {resetStep === 'working' && (
              <p className="mt-3 text-sm text-amber-800">Resetting password…</p>
            )}

            {resetError && (
              <p className="mt-3 text-sm text-red-700">{resetError}</p>
            )}

            {tempPassword && (
              <div className="mt-3 p-3 bg-white border border-amber-300 rounded-md">
                <p className="text-sm text-gray-800">
                  Temporary password — <strong>shown only once</strong>, pass it to the
                  customer now. They must change it when they next log in.
                </p>
                <div className="mt-2 flex items-center space-x-3">
                  <code className="text-lg font-mono bg-gray-100 px-3 py-1.5 rounded border border-gray-300">
                    {tempPassword}
                  </code>
                  <button
                    onClick={handleCopyTempPassword}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {tempPasswordCopied ? 'Copied ✓' : 'Copy'}
                  </button>
                  <button
                    onClick={() => {
                      setTempPassword(null);
                      setResetError(null);
                      setTempPasswordCopied(false);
                    }}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
          {emailCodes && emailCodes.status !== 'no_login' && (
            <div className="mt-4 p-4 bg-sky-50 border border-sky-200 rounded-md">
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div>
                  <p className="text-sm font-medium text-sky-900">Email code sign-in</p>
                  {stuckOnPassword ? (
                    <p className="text-sm text-sky-800 mt-1">
                      On for <strong>{emailCodes.signInEmail}</strong>, but they have a temporary password
                      that must be changed. After signing in by code they would be asked for it, so
                      replace it before they next sign in.
                    </p>
                  ) : emailCodes.status === 'enabled' ? (
                    <p className="text-sm text-sky-800 mt-1">
                      On for <strong>{emailCodes.signInEmail}</strong>. The customer enters this email on the
                      login page and chooses <strong>Email me a code</strong>, then types the 6-digit code from the email.
                    </p>
                  ) : emailCodes.status === 'available' ? (
                    <p className="text-sm text-sky-800 mt-1">
                      For customers who struggle with passwords: lets them sign in with a 6-digit code
                      emailed to <strong>{emailCodes.email}</strong>.
                    </p>
                  ) : emailCodes.status === 'placeholder' ? (
                    <p className="text-sm text-sky-800 mt-1">
                      Their login still uses an imported placeholder email, so email codes can't be
                      turned on here. They can add their real email after signing in with their
                      password (Sign-in options).
                    </p>
                  ) : emailCodes.status === 'email_mismatch' ? (
                    <p className="text-sm text-sky-800 mt-1">
                      Their sign-in email is <strong>{emailCodes.signInEmail}</strong> but the email above
                      is <strong>{emailCodes.email || 'blank'}</strong>. If {emailCodes.signInEmail} is
                      correct, set it with Edit Details, then turn on email codes.
                    </p>
                  ) : (
                    <p className="text-sm text-sky-800 mt-1">
                      Their sign-in email <strong>{emailCodes.signInEmail}</strong> can't receive sign-in codes.
                    </p>
                  )}
                </div>
                {canApply && emailCodesStep === 'idle' && (
                  <button
                    onClick={() => { setEmailCodesStep('confirm'); setEmailCodesError(null); }}
                    className="bg-sky-700 text-white py-2 px-4 rounded-md hover:bg-sky-800 shrink-0"
                  >
                    {stuckOnPassword ? 'Replace temporary password' : 'Turn on email codes'}
                  </button>
                )}
              </div>

              {canApply && emailCodesStep === 'confirm' && (
                <div className="mt-3 p-3 bg-white border border-sky-300 rounded-md space-y-2">
                  {!stuckOnPassword && <p className="text-sm text-gray-800">
                    Only continue if you have checked with <strong>{customer.name || customer.username}</strong> that{' '}
                    <strong>{emailCodes.email}</strong> is their address. Anyone who can read that inbox
                    will be able to sign in to this account.
                  </p>}
                  {emailCodes.passwordChangePending && (
                    <p className="text-sm text-gray-800">
                      Their current password is temporary (after a reset, or imported). It will stop
                      working and they will be signed out everywhere; they can set a password later
                      with Forgot password.
                    </p>
                  )}
                  <div className="pt-1 flex space-x-3">
                    <button
                      onClick={handleEnableEmailCodes}
                      className="bg-sky-700 text-white py-2 px-4 rounded-md hover:bg-sky-800"
                    >
                      {stuckOnPassword ? 'Yes, replace it' : "Yes, it's their email"}
                    </button>
                    <button
                      onClick={() => setEmailCodesStep('idle')}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {emailCodesStep === 'working' && (
                <p className="mt-3 text-sm text-sky-800">Saving…</p>
              )}

              {emailCodesError && (
                <p className="mt-3 text-sm text-red-700">{emailCodesError}</p>
              )}
            </div>
          )}
        </div>

        {id && (
          <SendAppNotification
            userId={customerId}
            hasPushToken={!!customerData?.user?.expoPushToken}
          />
        )}

        {/* Site Popup Management */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-medium font-sans">Site Popups</h2>
            <button
              onClick={() => setShowPopupForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={showPopupForm || hasReachedPopupLimit}
              style={{ display: !showPopupForm ? "block" : "none" }}
            >
              Create Popup
            </button>
          </div>

          <div className="mb-4 text-sm text-gray-600">
            {popupCount}/{MAX_SITE_POPUPS_PER_CUSTOMER} popups used
          </div>

          {hasReachedPopupLimit && (
            <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Popup limit reached. Deactivate/delete an existing popup before creating a new one.
            </div>
          )}

          {showPopupForm && !hasReachedPopupLimit && (
            <form onSubmit={handleCreatePopup} className="mb-6 p-4 border rounded-lg bg-gray-50">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={popupForm.title}
                    onChange={(e) => setPopupForm({ ...popupForm, title: e.target.value })}
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    value={popupForm.message}
                    onChange={(e) => setPopupForm({ ...popupForm, message: e.target.value })}
                    required
                    rows={3}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number (optional)
                  </label>
                  <input
                    type="tel"
                    value={popupForm.phoneNumber}
                    onChange={(e) => setPopupForm({ ...popupForm, phoneNumber: e.target.value })}
                    placeholder="e.g., 0800 123 4567"
                    className="w-full"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="show_phone"
                    checked={popupForm.showPhone}
                    onChange={(e) => setPopupForm({ ...popupForm, showPhone: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label htmlFor="show_phone" className="ml-2 text-sm text-gray-700">
                    Show phone number in popup
                  </label>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowPopupForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Create Popup
                  </button>
                </div>
              </div>
            </form>
          )}

          {popups && popups.length > 0 ? (
            <div className="space-y-3">
              {popups.map((popup: Popup) => (
                <div key={popup._id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row">
                        <h3 className="font-medium font-sans">{popup.title}</h3>
                        <div className="pl-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${popup.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                            }`}>
                            {popup.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm mt-1">{popup.message}</p>
                      {popup.phoneNumber && (
                        <p className="text-sm text-gray-500 mt-2">
                          Phone: {popup.phoneNumber} {!popup.showPhone && '(hidden)'}
                        </p>
                      )}
                      <div className="flex space-x-4 mt-3 text-sm text-gray-500">
                        <span>Views: {popup.viewCount}</span>
                        <span>Dismissed: {popup.dismissCount}</span>
                        <span>Calls: {popup.callCount}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">

                      <button
                        onClick={() => handleTogglePopup(popup._id, popup.isActive)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        {popup.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeletePopup(popup._id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              No popups created for this site yet.
            </p>
          )}
        </div>
        {/* ADMIN - Profile Updates */}
        <div id="pending-profile-updates" className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-medium font-sans">Pending Profile Updates</h2>
            <span className="text-sm text-gray-500">{pendingProfileUpdates?.length ?? 0} pending</span>
          </div>

          {!pendingProfileUpdates ? (
            <p className="text-gray-500 text-center py-4">Loading pending profile updates...</p>
          ) : pendingProfileUpdates.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No pending profile updates for this customer.</p>
          ) : (
            <div className="space-y-3">
              {pendingProfileUpdates.map((submission: any) => {
                return (
                  <div
                    key={submission._id}
                    className={`rounded-lg border p-4  border-blue-400 bg-blue-50
                      }`}
                  >
                    {(submission?.requestedByUsername && submission.requestedByUsername) &&
                      <div className="font-semibold font-sans text-gray-900">
                        {submission?.requestedByUsername ? submission.requestedByUsername : 'No username'}:
                      </div>
                    }
                    {(submission?.changes?.name
                      && submission.changes.name) &&
                      <div className="text-sm text-gray-600 ">
                        {submission?.changes?.name
                          ? submission.changes.name
                          : 'Unknown customer'}
                      </div>}


                    {(submission?.changes?.address && submission.changes.address) &&
                      <div className="mt-2 text-sm text-gray-700">
                        Address: {submission?.changes?.address ? submission.changes.address : 'Not provided'}
                      </div>
                    }
                    {(submission?.changes?.email && submission.changes.email) &&
                      <div className="mt-2 text-sm text-gray-700">
                        Primary Email: {submission?.changes?.email ? submission.changes.email : 'Not provided'}
                      </div>
                    }
                    {(submission?.changes?.email2 && submission.changes.email2) &&
                      <div className="mt-2 text-sm text-gray-700">
                        Secondary Email: {submission?.changes?.email2 ? submission.changes.email2 : 'Not provided'}
                      </div>
                    }
                    {(submission?.changes?.mobileNumber && submission.changes.mobileNumber) &&
                      <div className="mt-2 text-sm text-gray-700">
                        Mobile: {submission?.changes?.mobileNumber ? submission.changes.mobileNumber : 'Not provided'}
                      </div>
                    }
                    {(submission?.changes?.telephoneNumber && submission.changes.telephoneNumber) &&
                      <div className="mt-2 text-sm text-gray-700">
                        Telephone: {submission?.changes?.telephoneNumber ? submission.changes.telephoneNumber : 'Not provided'}
                      </div>
                    }
                    {submission.status === 'pending' && (
                      <div className="flex gap-2 flex-row-reverse">
                        <button
                          type="button"
                          onClick={() => handleReviewUpdateProfileSubmission(submission._id, 'declined')}
                          className="rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReviewUpdateProfileSubmission(submission._id, 'accepted')}
                          className="rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
                        >
                          Approve
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>



        {/* ADMIN - METER Submissions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-medium font-sans">Meter Submissions</h2>
            <span className="text-sm text-gray-500">
              {meterSubmissions?.length ?? 0} submissions
            </span>
          </div>

          {id && (
            <MeterReadingRequest userId={customerId} hasPushToken={!!customerData?.user?.expoPushToken} />
          )}

          {meterSubmissions && meterSubmissions.length > 0 ? (
            <div className="space-y-4">
              {meterSubmissions.map((submission: any) => (
                <div key={submission._id} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-col gap-4 lg:flex-row">
                    <div className="lg:w-64">
                      {submission.storageUrl ? (
                        <img
                          src={submission.storageUrl}
                          alt="Meter submission"
                          className="h-48 w-full rounded-md object-cover"
                        />
                      ) : (
                        <div className="flex h-48 items-center justify-center rounded-md bg-gray-100 text-sm text-gray-500">
                          Image unavailable
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-sm text-gray-500">
                            Submitted {new Date(submission.submittedAt).toLocaleString('en-GB')}
                          </div>
                          <div className="mt-1 text-sm uppercase tracking-wide text-gray-700">
                            {submission.status} • {submission.source}
                          </div>
                        </div>
                        {submission.status === 'pending' && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleReviewMeterSubmission(submission._id, 'approved')}
                              className="rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => handleReviewMeterSubmission(submission._id, 'rejected')}
                              className="rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>

                      <dl className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                          <dt className="text-xs font-medium font-sans uppercase tracking-wide text-gray-500">
                            Submitted Serial
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {submission.submittedMeterSerialNumber || 'Not provided'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium font-sans uppercase tracking-wide text-gray-500">
                            OCR Serial
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {submission.extractedMeterSerialNumber || 'Not detected'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium font-sans uppercase tracking-wide text-gray-500">
                            Submitted Reading
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {submission.submittedMeterReading || 'Not provided'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium font-sans uppercase tracking-wide text-gray-500">
                            OCR Reading
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {submission.extractedMeterReading || 'Not detected'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium font-sans uppercase tracking-wide text-gray-500">
                            Submitted OBIS
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {submission.submittedMeterOBISCode || 'Not provided'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium font-sans uppercase tracking-wide text-gray-500">
                            OCR OBIS
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {submission.extractedMeterOBISCode || 'Not detected'}
                          </dd>
                        </div>
                      </dl>

                      {submission.reviewerNotes && (
                        <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                          <span className="font-medium">Reviewer notes:</span> {submission.reviewerNotes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              No meter submissions for this customer yet.
            </p>
          )}
        </div>

        {/* Quick Facts */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-medium font-sans mb-4 text-center">Quick Facts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Generated:</p>
              <p className="text-2xl font-medium font-sans">{readings.totalGenerated.toLocaleString()} kWh</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">CO₂:</p>
              <p className="text-2xl font-medium font-sans">{co2Saved}t</p>
            </div>
          </div>
        </div>

        {/* Readings Table */}
        <div className="mb-12">
          <h2 className="text-2xl font-light mb-6">Table - Previous 7 days</h2>
          <ReadingsTable readings={formattedWeekly} />
        </div>

        {/* Readings Chart */}
        <div>
          <h2 className="text-2xl font-light mb-6">Graph - Previous 31 Days</h2>
          <ReadingsChart readings={formattedMonthly} />
        </div>
      </div>
    </Layout>
  );
};

export default AdminCustomerPage;
